import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type React from "react";
import type { TestContext } from "../../../types";

interface TestStepsFloatingActionBarProps {
  selectedContexts: TestContext[];
  onDelete: (contexts: TestContext[]) => void;
  onDuplicate: (contexts: TestContext[]) => void;
  onToggleDisable: (contexts: TestContext[]) => void;
}

export const TestStepsFloatingActionBar: React.FC<TestStepsFloatingActionBarProps> = ({
  selectedContexts,
  onDelete,
  onDuplicate,
  onToggleDisable,
}) => {
  const handleDelete = () => {
    onDelete(selectedContexts);
  };

  const handleDuplicate = () => {
    onDuplicate(selectedContexts);
  };

  const handleToggleDisable = () => {
    onToggleDisable(selectedContexts);
  };

  const hasDisabledContexts = selectedContexts.some((ctx) => ctx.disabled);
  const hasEnabledContexts = selectedContexts.some((ctx) => !ctx.disabled);

  if (selectedContexts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        animation: "slideUp 0.2s ease-out",
        "@keyframes slideUp": {
          from: {
            opacity: 0,
            transform: "translateX(-50%) translateY(20px)",
          },
          to: {
            opacity: 1,
            transform: "translateX(-50%) translateY(0)",
          },
        },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          borderRadius: 2,
          backgroundColor: "text.primary",
          color: "white",
          px: 2,
          py: 1,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ mr: 1, fontSize: 12 }}>
            {selectedContexts.length} selected
          </Typography>

          <Tooltip
            title={
              hasDisabledContexts && hasEnabledContexts
                ? "Toggle all"
                : hasDisabledContexts
                  ? "Enable all"
                  : "Disable all"
            }
          >
            <IconButton
              size="small"
              onClick={handleToggleDisable}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {hasDisabledContexts ? (
                <ToggleOnIcon fontSize="small" />
              ) : (
                <ToggleOffIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Duplicate">
            <IconButton
              size="small"
              onClick={handleDuplicate}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
    </Box>
  );
};
